import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { Loader } from "@progress/kendo-react-indicators";
import { AxiosError } from "axios";
import { useEffect, useRef, useState } from "react";
import CustomComboBox from "../../../components/custom/form/CustomComboBox";
import CustomFormTextArea from "../../../components/custom/form/CustomFormTextArea";
import CustomInput from "../../../components/custom/form/CustomInput";
import useAuth from "../../../hooks/useAuth";
import useLocale from "../../../hooks/useLocale";
import useMasterData from "../../../hooks/useMasterData";
import customerService from "../../../services/customer.service";
import { Customer } from "../../../types/customer/Customer";
import "./AccountSettings.scss";
import { UpsertCustomer } from "../../../types/customer";
import useSwal from "../../../hooks/useSwal";
import { Link } from "react-router-dom";
import { CustomerMfaModes } from "../../../types/customer/CustomerMfaModes";
import { UpsertCustomerMfaConfig } from "../../../types/customer/UpsertCustomerMfaConfig";
import CustomSwitch from "../../../components/custom/form/CustomSwitch";
import { Switch, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import useTranslation from "../../../hooks/useTranslation";
import { UpsertCustomerConfiguration } from "../../../types/customer/CustomerConfiguration";
import Swal, { SweetAlertOptions } from "sweetalert2";
import { User } from "../../../types/user";
import { Checkbox } from "@progress/kendo-react-inputs";
import SettingService from "../../../services/setting.service";
import { Tooltip } from "@progress/kendo-react-tooltip";

interface AccountSettingsProps {
  customerInfo: Customer | undefined;
  setCustomerInfo: React.Dispatch<React.SetStateAction<Customer | undefined>>;
  customerError: string | undefined;
  pageFreezeLoader: boolean;
}

const AccountSettings: React.FC<AccountSettingsProps> = ({
  customerInfo,
  customerError,
  setCustomerInfo,
  pageFreezeLoader,
}) => {
  const trans = useTranslation("AccountSettings");
  const localeCtx = useLocale();
  const auth = useAuth();
  const swal = useSwal();
  const masterData = useMasterData();
  const [loading, setLoading] = useState<boolean>(pageFreezeLoader);
  const [error, setError] = useState<string>();
  const [editing, setEditing] = useState<boolean>(false);
  const [updating, setUpdating] = useState<boolean>(false);
  const formRef = useRef<any>();
  const [buttonStateTracker, setButtonStateTracker] = useState<boolean>(true);
  const [customerMFA, setCustomerMFA] = useState<CustomerMfaModes>();
  const [checkMFAEnabled, setCheckMFAEnabled] = useState<boolean>(false);
  const [checkAutoOutcomeEnabled, setCheckAutoOutcomeEnabled] = useState<boolean | undefined>(false);
  const [isRedCactusEnabled, setIsRedCactusEnabled] = useState<boolean>(false);
  const [redCactusSwitchValue, setRedCactusSwitchValue] = useState<boolean>(false);
  const [redCactusEnabledAllUsers, setRedCactusEnabledAllUsers] = useState<boolean>(false);
  const [recCactusTokenPresent, setRedCactusTokenPresent] = useState<boolean>(false);
  const [isRedCactusEnabledInstanceLevel, setIsRedCactusEnabledInstanceLevel] = useState<boolean|undefined>(undefined);
  const [redCactusTokenEditing, setRedCactusTokenEditing] = useState<boolean>(false);
  const [redCactusTokenSaved, setRedCactusTokenSaved] = useState<boolean>(false);

  useEffect(() => {
    if (
      !localeCtx?.selectedLocale?.current.componentTranslations[
        "AccountSettings"
      ]
    ) {
      trans.fetchTranslations("AccountSettings");
    }
  }, [localeCtx?.selectedLocale]);

  useEffect(()=>{
    const getRedCactusFromSystemSettings=async()=>{
      try{
        const res = await SettingService.getRedCactusIsEnabled();
        if(res==="false"){
          setIsRedCactusEnabled(false);
          setRedCactusSwitchValue(false);
          setRedCactusEnabledAllUsers(false);
          setIsRedCactusEnabledInstanceLevel(false)
          setRedCactusTokenEditing(false);
          setRedCactusTokenSaved(false);
        }else{
          setIsRedCactusEnabledInstanceLevel(true);
        }
      }catch(err){
        setIsRedCactusEnabled(false);
        setRedCactusSwitchValue(false);
        setRedCactusEnabledAllUsers(false);
        setIsRedCactusEnabledInstanceLevel(false);
        setRedCactusTokenEditing(false);
        setRedCactusTokenSaved(false);
      }
    }
    getRedCactusFromSystemSettings();
  },[auth?.tokenPayload?.CustomerId])

  useEffect(() => {
    const getMFA = async () => {
      const customerId = auth?.tokenPayload?.CustomerId;
      if (customerId !== undefined && customerId > 0) {
        try {
          setError(undefined);
          const custMFA = await customerService.getCustomerMFAModes(customerId);
          setCustomerMFA(custMFA);
          if (
            custMFA.mfaModeNames &&
            custMFA.mfaModeNames.length &&
            custMFA.mfaModeNames?.length > 0
          ) {
            setCheckMFAEnabled(true);
            const form = formRef.current as Form;
            form.values.mfaEnabled = true;
          } else {
            setCheckMFAEnabled(false);
          }
        } catch (err) {
          if (err instanceof AxiosError) {
            setError(err.response?.data?.error ?? err.message);
          }
        }
      }
    };

    const getCustomerConfig = async () => {
      const customerId = auth?.tokenPayload?.CustomerId;
      if (customerId !== undefined && customerId > 0){
        try{          
          setError(undefined);
          const custConfig = await customerService.getCustomerConfiguration(customerId);
          setCheckAutoOutcomeEnabled(custConfig.autoOutcomeEnabled);
          setIsRedCactusEnabled(custConfig.redCactusEnabled);
          setRedCactusSwitchValue(custConfig.redCactusEnabled);
          setRedCactusEnabledAllUsers(custConfig.redCactusEnabledAllUsers);
          setRedCactusTokenPresent(custConfig.redCactusTokenPresent);
        }  catch (err) {
          if (err instanceof AxiosError) {
            setError(err.response?.data?.error ?? err.message);
          }
        } 
      }
    }

    const getCustomerDetails = async () => {
      try{
        setLoading(true);
      
        await getMFA();
        await getCustomerConfig();
      } finally {
        setLoading(false);
      }
    }

    getCustomerDetails();
    
  }, [auth?.tokenPayload?.CustomerId]);

  const userEditClickHandler = () => {
    setEditing(true);
  };

  const onUserEditCancelHandler = () => {
    setEditing(false);
    const form = formRef.current as Form;
    form.resetForm();
    setRedCactusTokenSaved(false);
    setRedCactusTokenEditing(false);
    setRedCactusSwitchValue(isRedCactusEnabled);
  };

  const isCustomerDataUpdated = (
    customerInfo: Customer,
    dataItem: { [name: string]: any }
  ): boolean => {
    if (
      customerInfo.name === dataItem.customerName &&
      customerInfo.email === dataItem.email &&
      customerInfo.address === dataItem.customerAddress &&
      customerInfo.contact === dataItem.contact &&
      customerInfo.timezone?.displayName === dataItem.timezone.displayName &&
      customerInfo.locale?.code === dataItem.locale.code &&
      customerInfo.country?.name === dataItem.country.name
    ) {
      return false;
    }

    return true;
  };

  const onSubmitHandler = async (dataItem: { [name: string]: any }) => {
    try {
      if (customerInfo) {
        const isCustomerUpdated: boolean = isCustomerDataUpdated(
          customerInfo,
          dataItem
        );
        const isMFAUpdated: boolean =
          checkMFAEnabled === dataItem.mfaEnabled ? false : true;

        const isAutoOutcomeUpdated: boolean =
          checkAutoOutcomeEnabled === dataItem.autoOutcomeEnabled ? false : true;
        
        const isRedCactusUpdated: boolean =
          (isRedCactusEnabled === dataItem.redCactusEnabled ? false : true) ||
          (redCactusEnabledAllUsers === dataItem.redCactusAllUsers ? false : true);

        if (isCustomerUpdated) {
          setUpdating(true);
          let updatedCustomer: UpsertCustomer = {
            name: dataItem.customerName,
            email: dataItem.email,
            address: dataItem.customerAddress,
            contact: dataItem.contact,
            timezoneidentifier: dataItem.timezone.timezoneIdentifier,
            localeCode: dataItem.locale.code,
            countryName: dataItem.country.name,
            externalCustomerInfo: customerInfo.externalCustomerInfo,
          };
          const updatedCustomerResponse =
            await customerService.updateCustomerDetailsById(
              customerInfo?.id,
              updatedCustomer
            );
          setCustomerInfo(updatedCustomerResponse);
        }

        if (isMFAUpdated) {
          const upsertMFA: UpsertCustomerMfaConfig = {
            CustomerMfaConfigList: [],
          };
          if (dataItem.mfaValues) {
            dataItem.mfaValues.forEach((u: string) => {
              upsertMFA.CustomerMfaConfigList.push({
                MfaModeName: u,
                IsEnabled: dataItem.mfaEnabled,
              });
            });
          }
          if (dataItem.mfaEnabled) {
            upsertMFA.CustomerMfaConfigList.push({
              MfaModeName: "EmailAuthentication",
              IsEnabled: dataItem.mfaEnabled,
            });
          } else {
            upsertMFA.CustomerMfaConfigList = [];
          }

          const updatedMFAResponse = await customerService.upsertMFAModes(
            customerInfo?.id,
            upsertMFA
          );
          if (
            upsertMFA.CustomerMfaConfigList &&
            upsertMFA.CustomerMfaConfigList.length > 0
          ) {
            let custMFAModes: CustomerMfaModes = { mfaModeNames: [] };
            custMFAModes.mfaModeNames?.push(
              upsertMFA.CustomerMfaConfigList[0].MfaModeName
            );
            setCustomerMFA(custMFAModes);
            setCheckMFAEnabled(true);
          } else {
            setCheckMFAEnabled(false);
          }
        }


        const form = formRef.current as Form;
        const redcactusToken = dataItem.redcactusToken;
        form.values.redcactusToken = "";

        if(isAutoOutcomeUpdated || isRedCactusUpdated || redCactusTokenSaved){
          var customerConfig: UpsertCustomerConfiguration = {
            customerName:customerInfo.name,
            autoOutcomeEnabled: dataItem.autoOutcomeEnabled,
            redCactusConfig:{
              redCactusEnabled:dataItem.redCactusEnabled,
              enabledForAllUsers:dataItem.redCactusAllUsers,
              redCactusToken:redcactusToken,
              redCactusTokenSaved:redCactusTokenSaved
            }
          }

          var autoOutcomeUpdate = customerService.upsertCustomerConfiguration(customerInfo?.id, customerConfig);

          setCheckAutoOutcomeEnabled(dataItem.autoOutcomeEnabled);
          setIsRedCactusEnabled(dataItem.redCactusEnabled);
          setRedCactusEnabledAllUsers(dataItem.redCactusAllUsers);
          setRedCactusSwitchValue(dataItem.redCactusEnabled);
          if(redcactusToken != "")
          {
            setRedCactusTokenPresent(true);
          }else{
            setRedCactusTokenPresent(false);
          }
        }

        setEditing(false);
        setRedCactusTokenEditing(false);
        setRedCactusTokenSaved(false);        

        masterData?.fetchCustomerConfigData();

        setButtonStateTracker(true);
        
        if (isCustomerUpdated || isMFAUpdated || isAutoOutcomeUpdated || isRedCactusUpdated || redCactusTokenSaved) {
          swal.fire({
            icon: "success",
            confirmButtonText: `${trans.fetchLabelKeyTranslation(
              "SwtAltOkText",
              "Ok"
            )}`,
            title: `${trans.fetchLabelKeyTranslation(
              "SwtAltAccountDetailsUpdated",
              "Account Details Updated"
            )}`,
          });
        }
      }
    } catch (err) {
      if (err instanceof AxiosError) {
        console.error(err);
        swal.fire({
          icon: "error",
          title: `${trans.fetchLabelKeyTranslation(
            "SwtAltErrorText",
            "Error"
          )}`,
          text: `${err.message}`,
        });
      }
    } finally {
      setUpdating(false);
    }
  };

  const compareState = () => {
    const form = formRef.current as Form;
    if (
      form.values.locale?.id === customerInfo?.locale?.id &&
      (form.values.timezone
        ? form.values.timezone.id === customerInfo?.timezone?.id
        : true) &&
      (form.values.country
        ? form.values.country.id === customerInfo?.country?.id
        : true) &&
      form.values.customerAddress == customerInfo?.address &&
      form.values.contact == customerInfo?.contact &&
      form.values.email == customerInfo?.email &&
      form.values.customerName == customerInfo?.name &&
      form.values.mfaEnabled == checkMFAEnabled &&
      form.values.redCactusEnabled == isRedCactusEnabled &&
      form.values.autoOutcomeEnabled == checkAutoOutcomeEnabled &&
      !redCactusTokenSaved &&
      form.values.redCactusAllUsers === redCactusEnabledAllUsers
    ) {
      setButtonStateTracker(true);
    } else {
      setButtonStateTracker(false);
    }
  };

  const printMfaModeNames = (modeNames: string[] | undefined): string => {
    let translatedNames: string[] = [];
    if (modeNames && modeNames?.length > 0) {
      modeNames.forEach((value, index) => {
        switch (value) {
          case "EmailAuthentication": {
            translatedNames.push(
              trans.fetchLabelKeyTranslation(
                "EmailAuthenticationText",
                "Email Authentication"
              )
            );
            break;
          }
          default:
            translatedNames.push("");
            break;
        }
      });
    }

    const names = translatedNames.join(", ");
    return names;
  };

  useEffect(()=>{
    const form = formRef.current as Form;
    if(form)
      compareState();
  },[redCactusTokenSaved]);

  const saveRedCactusToken = (formRenderProps : FormRenderProps) => {
    setRedCactusTokenSaved(true);
    setRedCactusTokenEditing(false);
    formRenderProps.onChange('redCactusTokenSaved', {value:true});
  };

  const cancelRedCactusToken = () => {
    const form = formRef.current as Form;
    form.values.redcactusToken = "";
    setRedCactusTokenEditing(false);
    setRedCactusTokenSaved(false);    
  };

  const redCactusOnChangeHandler = async (event: SwitchChangeEvent) => {
    const checked = event.value;
    const findUser : User|undefined = masterData?.standardUsers.find((_u:User)=>_u.id === auth?.user?.id);
    let res;
    if(checked){
      const swalOptions: SweetAlertOptions = {
        icon: "question",
        title: trans.fetchLabelKeyTranslation("RedCactusEnableAllUserQues","Do you want to enable RedCactus Bubble for all users?"),
        showCancelButton: true,
        confirmButtonText: trans.fetchLabelKeyTranslation("SwtAltYesText","Yes"),
        cancelButtonText: trans.fetchLabelKeyTranslation("SwtAltNoText","No"),
        showCloseButton:true,
      };
      res = await swal.fire(swalOptions);
    }
    if(res?.dismiss===Swal.DismissReason.close){
      return;
    }
    const customerConfigData:UpsertCustomerConfiguration={
      customerName:customerInfo?.name ?? "",
      autoOutcomeEnabled:checkAutoOutcomeEnabled,
      redCactusConfig:{
        redCactusEnabled:checked,
        enabledForAllUsers:res?.isConfirmed ?? false
      }
    }
    try{
      await customerService.upsertCustomerConfiguration(customerInfo?.id ?? 0,customerConfigData);
      const swalOptions: SweetAlertOptions = {
        icon: "success",
        title: checked ? trans.fetchLabelKeyTranslation("RedCactusEnabledSuccessText","RedCactus Bubble enabled") : trans.fetchLabelKeyTranslation("RedCactusDisabledSuccessText","RedCactus Bubble disabled"),
        confirmButtonText: trans.fetchLabelKeyTranslation("SwtAltOkText","Ok"),
      };
      swal.fire(swalOptions);
      setIsRedCactusEnabled(checked);
      masterData?.setIsRedCactusActive(((findUser && findUser.redCactusEnabled) || res?.isConfirmed)  && checked ? true : false);
    }catch(error){
      if (error instanceof AxiosError) {
        const swalOptions: SweetAlertOptions = {
        icon: "error",
        title: trans.fetchLabelKeyTranslation("RedCactusUpdateFailureText","RedCactus Bubble cannot be updated at the moment. Please try again later."),
        confirmButtonText: trans.fetchLabelKeyTranslation("SwtAltOkText","Ok"),
      };
      swal.fire(swalOptions);
      }
    }
  };

  return (
    <div className="m-b-10">
      <div className="row">
        <div className="col-md-12">
          <div className="trk-container emlBox">
            <div className="trk-inr p-b-30">
              <div className="trk-t text-black-14 border-bottom-solid border-w-1 border-black-1">
                <span className="text-primary">
                  {trans.fetchLabelKeyTranslation(
                    "AccountSettingsTitle",
                    "Accounts Details"
                  )}
                </span>
                {!editing && !error && !customerError && (
                  <span className="float-right">
                    <Button
                      className={`btn bg-transparent text-black-12 text-primary m-r-5 p-r-7 p-l-7 ${
                        masterData?.telepoDisableConfig?.systemSetting.account
                          .disableChanges
                          ? "disabledBtn"
                          : "border-primary"
                      }`}
                      style={{ height: "26px" }}
                      onClick={userEditClickHandler}
                      disabled={
                        masterData?.telepoDisableConfig?.systemSetting.account
                          .disableChanges
                      }
                    >
                      <span className="p-r-5">
                        {trans.fetchLabelKeyTranslation(
                          "AccountSettingsTabEdit",
                          "Edit"
                        )}
                      </span>
                      <i className="bi bi-pencil"></i>
                    </Button>
                  </span>
                )}
              </div>
              {(loading || !masterData?.data) && (
                <div className="m-t-30" style={{ textAlign: "center" }}>
                  <Loader type={"infinite-spinner"} />
                </div>
              )}
              {!loading && (error || customerError) && (
                <div className="float-left w-100 m-t-10">
                  <div className="selectScorecard">
                    <div>
                      <span className="tx-red fs-14">
                        {customerError || error}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              {!loading && !error && !customerError && masterData?.data && (
                <Form
                  onSubmit={onSubmitHandler}
                  ref={formRef}
                  initialValues={{
                    customerName:
                      customerInfo?.externalCustomerInfo?.customerName ??
                      customerInfo?.name,
                    customerAddress: customerInfo?.address,
                    country:
                      masterData?.data?.countries.find(
                        (c) => c.id === customerInfo?.country?.id
                      ) ?? customerInfo?.country,
                    timezone:
                      masterData?.data?.timezones.find(
                        (c) => c.id === customerInfo?.timezone?.id
                      ) ?? customerInfo?.timezone,
                    locale: customerInfo?.locale,
                    email: customerInfo?.email,
                    contact: customerInfo?.contact,
                    mfaValues: customerMFA?.mfaModeNames,
                    mfaEnabled: checkMFAEnabled,
                    autoOutcomeEnabled: checkAutoOutcomeEnabled,
                    redCactusEnabled: isRedCactusEnabled,
                    redCactusAllUsers: redCactusEnabledAllUsers,
                    redCactusTokenSaved: false
                  }}
                  render={(formRenderProps: FormRenderProps) => (
                    <FormElement>
                      <div>
                        <div className="trk-row border-bottom-solid border-w-1 border-black-1">
                          <div className="trkCol">
                            <div className="trkCol-h font-weight-semi">
                              {trans.fetchLabelKeyTranslation(
                                "AccountSettingsCompanyName",
                                "Company Name"
                              )}
                            </div>
                            <div className="trkCol-dot">:</div>
                            <div className="trkCol-p">
                              {!editing ? (
                                <span>
                                  {customerInfo?.externalCustomerInfo
                                    ?.customerName ?? customerInfo?.name}
                                </span>
                              ) : (
                                <Field
                                  className="textInput"
                                  id={"customerName"}
                                  name={"customerName"}
                                  value={formRenderProps.valueGetter(
                                    "customerName"
                                  )}
                                  component={CustomInput}
                                  disabled={
                                    customerInfo?.externalCustomerInfo !== null
                                  }
                                  onChange={compareState}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="trk-row border-bottom-solid border-w-1 border-black-1">
                          <div className="trkCol">
                            <div className="trkCol-h font-weight-semi">
                              {trans.fetchLabelKeyTranslation(
                                "AccountSettingsEmail",
                                "Email"
                              )}
                            </div>
                            <div className="trkCol-dot">:</div>
                            <div className="trkCol-p">
                              {!editing ? (
                                <span>{customerInfo?.email}</span>
                              ) : (
                                <Field
                                  id={"email"}
                                  name={"email"}
                                  className="comboBoxInput"
                                  textField="email"
                                  value={formRenderProps.valueGetter("email")}
                                  component={CustomInput}
                                  onChange={compareState}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="trk-row border-bottom-solid border-w-1 border-black-1">
                          <div className="trkCol">
                            <div className="trkCol-h font-weight-semi">
                              {trans.fetchLabelKeyTranslation(
                                "AccountSettingsContact",
                                "Contact"
                              )}
                            </div>
                            <div className="trkCol-dot">:</div>
                            <div className="trkCol-p">
                              {!editing ? (
                                <span>{customerInfo?.contact}</span>
                              ) : (
                                <Field
                                  id={"contact"}
                                  name={"contact"}
                                  className="comboBoxInput"
                                  textField="contact"
                                  value={formRenderProps.valueGetter("contact")}
                                  component={CustomInput}
                                  onChange={compareState}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="trk-row border-bottom-solid border-w-1 border-black-1">
                          <div className="trkCol">
                            <div className="trkCol-h font-weight-semi">
                              {trans.fetchLabelKeyTranslation(
                                "AccountSettingsAddress",
                                "Address"
                              )}
                            </div>
                            <div className="trkCol-dot">:</div>
                            <div className="trkCol-p">
                              {!editing ? (
                                <span>{customerInfo?.address}</span>
                              ) : (
                                <Field
                                  id={"customerAddress"}
                                  name={"customerAddress"}
                                  style={{ height: "100px" }}
                                  className="comboBoxInput"
                                  placeholder={`${"Customer Address"}`}
                                  max={1000}
                                  autoSize={true}
                                  rows={15}
                                  showTextLimitHint={false}
                                  value={formRenderProps.valueGetter(
                                    "customerAddress"
                                  )}
                                  component={CustomFormTextArea}
                                  onChange={compareState}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="trk-row border-bottom-solid border-w-1 border-black-1">
                          <div className="trkCol">
                            <div className="trkCol-h font-weight-semi">
                              {trans.fetchLabelKeyTranslation(
                                "AccountSettingsCountry",
                                "Country"
                              )}
                            </div>
                            <div className="trkCol-dot">:</div>
                            <div className="trkCol-p">
                              {!editing ? (
                                <span>
                                  {
                                    masterData?.data?.countries.find(
                                      (ct) =>
                                        ct.id === customerInfo?.country?.id
                                    )?.localizationValue
                                  }
                                </span>
                              ) : (
                                <Field
                                  id={"country"}
                                  name={"country"}
                                  className="comboBoxInput"
                                  placeholder="Please select ..."
                                  data={masterData?.data?.countries ?? []}
                                  textField="localizationValue"
                                  value={formRenderProps.valueGetter("country")}
                                  loading={!masterData?.data?.countries}
                                  component={CustomComboBox}
                                  dataItemKey="id"
                                  sorting={true}
                                  sortField={"localizationValue"}
                                  onChange={compareState}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="trk-row border-bottom-solid border-w-1 border-black-1">
                          <div className="trkCol">
                            <div className="trkCol-h font-weight-semi">
                              {trans.fetchLabelKeyTranslation(
                                "AccountSettingsTimeZone",
                                "Time Zone"
                              )}
                            </div>
                            <div className="trkCol-dot">:</div>
                            <div className="trkCol-p">
                              {!editing ? (
                                <span>
                                  {
                                    masterData?.data?.timezones.find(
                                      (tz) =>
                                        tz.id === customerInfo?.timezone?.id
                                    )?.localizationValue
                                  }
                                </span>
                              ) : (
                                <Field
                                  id={"timezone"}
                                  name={"timezone"}
                                  className="comboBoxInput"
                                  placeholder="Please select ..."
                                  data={masterData?.data?.timezones ?? []}
                                  textField="localizationValue"
                                  value={formRenderProps.valueGetter(
                                    "timezone"
                                  )}
                                  loading={!masterData?.data?.timezones}
                                  component={CustomComboBox}
                                  dataItemKey="id"
                                  sorting={true}
                                  sortField={"localizationValue"}
                                  onChange={compareState}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="trk-row border-bottom-solid border-w-1 border-black-1">
                          <div className="trkCol">
                            <div className="trkCol-h font-weight-semi d-flex align-items-center">
                              {trans.fetchLabelKeyTranslation(
                                "AccountSettingsLanguage",
                                "Language"
                              )}
                            </div>
                            <div className="trkCol-dot d-flex align-items-center">
                              :
                            </div>
                            <div className="trkCol-p">
                              {!editing ? (
                                <span>
                                  {customerInfo?.locale?.displayName ?? "N/A"}
                                </span>
                              ) : (
                                <Field
                                  id={"locale"}
                                  name={"locale"}
                                  className="comboBoxInput"
                                  data={localeCtx?.supportedLocales ?? []}
                                  textField="displayName"
                                  value={formRenderProps.valueGetter("locale")}
                                  loading={!localeCtx?.supportedLocales}
                                  component={CustomComboBox}
                                  dataItemKey="id"
                                  sorting={true}
                                  sortField={"displayName"}
                                  onChange={compareState}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="trk-row border-bottom-solid border-w-1 border-black-1">
                          <div className="trkCol">
                            <div className="trkCol-h font-weight-semi d-flex align-items-center">
                              {trans.fetchLabelKeyTranslation(
                                "AccountSettingsMFA",
                                "MFA Modes"
                              )}
                            </div>
                            <div className="trkCol-dot d-flex align-items-center">
                              :
                            </div>
                            <div className="trkCol-p">
                              {!editing ? (
                                <span>
                                  {checkMFAEnabled ? (
                                    printMfaModeNames(customerMFA?.mfaModeNames)
                                  ) : (
                                    <div
                                      className="switchButton"
                                      title={`${trans.fetchLabelKeyTranslation(
                                        "MFADisabledText",
                                        "MFA Disabled"
                                      )}`}
                                    >
                                      <Switch checked={false} disabled={true} />
                                    </div>
                                  )}
                                </span>
                              ) : (
                                <span className="btnBlocks">
                                  <div
                                    className="switchButton"
                                    title={
                                      checkMFAEnabled
                                        ? `${trans.fetchLabelKeyTranslation(
                                            "MFAEnabledText",
                                            "MFA Enabled"
                                          )}`
                                        : `${trans.fetchLabelKeyTranslation(
                                            "MFADisabledText",
                                            "MFA Disabled"
                                          )}`
                                    }
                                  >
                                    <Field
                                      id={"mfaEnabled"}
                                      name={"mfaEnabled"}
                                      value={formRenderProps.valueGetter(
                                        "mfaEnabled"
                                      )}
                                      component={CustomSwitch}
                                      onChange={compareState}
                                    />
                                  </div>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="trk-row border-bottom-solid border-w-1 border-black-1">
                          <div className="trkCol">
                            <div className="trkCol-h font-weight-semi d-flex align-items-center">
                              {trans.fetchLabelKeyTranslation(
                                "AccountSettingsAutoOutcome",
                                "Auto Outcomes"
                              )}
                            </div>
                            <div className="trkCol-dot d-flex align-items-center">
                              :
                            </div>
                            <div className="trkCol-p">
                              {!editing ? (
                                <span>
                                  {checkAutoOutcomeEnabled ? (
                                    <div
                                    className="switchButton"
                                    title={`${trans.fetchLabelKeyTranslation(
                                      "AutoOutcomeEnabledText",
                                      "Auto Outcomes Enabled"
                                    )}`}
                                  >
                                    <Switch checked={true} disabled={false} />
                                  </div>
                                  ) : (
                                    <div
                                      className="switchButton"
                                      title={`${trans.fetchLabelKeyTranslation(
                                        "AutoOutcomeDisabledText",
                                        "Auto Outcomes Disabled"
                                      )}`}
                                    >
                                      <Switch checked={false} disabled={true} />
                                    </div>
                                  )}
                                </span>
                              ) : (
                                <span className="btnBlocks">
                                  <div
                                    className="switchButton"
                                    title={
                                      checkAutoOutcomeEnabled
                                        ? `${trans.fetchLabelKeyTranslation(
                                            "AutoOutcomeEnabledText",
                                            "Automatic Outcomes Enabled"
                                          )}`
                                        : `${trans.fetchLabelKeyTranslation(
                                            "AutoOutcomeDisabledText",
                                            "Automatic Outcomes Disabled"
                                          )}`
                                    }
                                  >
                                    <Field
                                      id={"autoOutcomeEnabled"}
                                      name={"autoOutcomeEnabled"}
                                      value={formRenderProps.valueGetter(
                                        "autoOutcomeEnabled"
                                      )}
                                      component={CustomSwitch}
                                      onChange={compareState}
                                    />
                                  </div>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {isRedCactusEnabledInstanceLevel && (<div>
                            <div className="trk-row">
                            <div className="trkCol">
                              <div className="trkCol-h font-weight-semi d-flex align-items-center">
                                {trans.fetchLabelKeyTranslation("RedCactusToggleTitle","Enable Bubble")}
                              </div>
                              <div className="trkCol-dot d-flex align-items-center">
                                :
                              </div>
                              <div className="trkCol-p">
                                {!editing ?(<span className="btnBlocks">                                
                                  {
                                    isRedCactusEnabled?
                                    (
                                    <div
                                      className="switchButton"
                                      title={`${trans.fetchLabelKeyTranslation(
                                        "RedCactusEnabledText",
                                        "Red Cactus Enabled"
                                      )}`}>                         
                                        <Switch checked={true} disabled={false} />
                                      </div>
                                    ):
                                    (
                                    <div
                                      className="switchButton"
                                      title={`${trans.fetchLabelKeyTranslation(
                                        "RedCactusDisabledText",
                                        "Red Cactus Disabled"
                                      )}`}>  
                                        <Switch checked={false} disabled={true} />
                                      </div>
                                    )
                                  }
                                  </span>):
                                (
                                <div className="d-flex flex-column">
                                  <div
                                        className="switchButton"
                                        title={
                                          isRedCactusEnabled
                                            ? `${trans.fetchLabelKeyTranslation(
                                                "RedCactusEnabledText",
                                                "Red Cactus Enabled"
                                              )}`
                                            : `${trans.fetchLabelKeyTranslation(
                                                "RedCactusDisabledText",
                                                "Red Cactus Disabled"
                                              )}`
                                        }
                                      >
                                        <Field
                                          id={"redCactusEnabled"}
                                          name={"redCactusEnabled"}
                                          value={formRenderProps.valueGetter(
                                            "redCactusEnabled"
                                          )}
                                          component={CustomSwitch}
                                          onChange={(e) => {
                                              setRedCactusSwitchValue(e.value);
                                              compareState();
                                            }}
                                        />

                                      </div>
                                </div>
                                )}
                                </div>
                              </div>
                            </div>                          
                          <div className="trk-row">
                            <div className="trkCol">
                              <div className="trkCol-h"></div>
                              <div className="trkCol-dot d-flex align-items-center">&nbsp;</div>
                              <div className="trkCol-p">                                
                                {redCactusSwitchValue && (<div className="redCactusAllUsers mb-2">
                                  <Field
                                    id={"redCactusAllUsers"}
                                    name={"redCactusAllUsers"}
                                    component={Checkbox}
                                    disabled={!editing}                                    
                                    onChange={compareState}
                                    className="mr-2"
                                    checked={formRenderProps.valueGetter("redCactusAllUsers")}
                                  /><span>{trans.fetchLabelKeyTranslation("RedCactusEnableAllUserQues", "Enable Bubble for all users")} </span>
                                </div>)}
                                {editing && redCactusSwitchValue && (<div className="redcactusBox">
                                  <span className="mb-1 fs-12">
                                    {trans.fetchLabelKeyTranslation("CallLoggingQueueToken",
                                      "Enter the Call Logging Queue token to send transcriptions to Red Cactus.")
                                    }
                                    <Tooltip
                                      anchorElement="target"
                                      position="top"
                                      parentTitle={true}
                                    >
                                      <i
                                        className="bi bi-info"
                                        title={trans.fetchLabelKeyTranslation(
                                          "CallLoggingQueueHelpToken",
                                          "Contact your Red Cactus Bubble admin to get your Call Logging Queue token."
                                        )}
                                      ></i>
                                    </Tooltip>
                                  </span>

                                  <div className="d-flex align-items-center mt-3">
                                    <div className="flex-grow-1">
                                      <Field
                                        id={"redcactusToken"}
                                        name={"redcactusToken"}
                                        className="comboBoxRedCactus"
                                        value={formRenderProps.valueGetter("redcactusToken")}
                                        component={CustomInput}
                                        disabled={!redCactusTokenEditing}
                                        placeholder={recCactusTokenPresent && !redCactusTokenEditing ? "******************************************************" : ""}
                                      />
                                    </div>

                                    <div className="ml-1">
                                      <Tooltip
                                        anchorElement="target"
                                        position="top"
                                        parentTitle={true}
                                      >
                                        {!redCactusTokenEditing && <Button className="btn btn-link text-primary px-2" title="Edit Token"
                                         onClick={()=>{setRedCactusTokenEditing(true);}} >
                                          <i className="bi bi-pencil fs-5"></i>
                                        </Button>}                                                                        
                                        {redCactusTokenEditing && 
                                        <div style={{display:"inline"}}>
                                          <Button type="button" className="btn btn-link text-success px-2" title="Save" onClick={() => {saveRedCactusToken(formRenderProps)}}>
                                            <i className="bi bi-check2 fs-5 me-2"></i>
                                          </Button>
                                          <Button type="button" className="btn btn-link text-danger px-2" title="Cancel" onClick={cancelRedCactusToken}>
                                            <i className="bi bi-x fs-5"></i>
                                          </Button>
                                        </div>
                                        }
                                      </Tooltip>
                                    </div>
                                  </div>



                                  {/* <Field
                                    id={"redcactusToken"}
                                    name={"redcactusToken"}
                                    className="comboBoxRedCactus mt-2"
                                    value={formRenderProps.valueGetter("redcactusToken")}
                                    component={CustomInput}
                                    onChange={compareState}
                                    placeholder={recCactusTokenPresent ? "******************************************************" : ""}
                                  /> */}
                                </div>)}
                              </div>
                            </div>
                          </div>
                        </div>)}
                        {editing && (
                          <div className="row p-t-8">
                            <div className="col-md-12 text-right">
                              <Button
                                className="btn bg-black-5 m-r-5"
                                onClick={onUserEditCancelHandler}
                              >
                                {trans.fetchLabelKeyTranslation(
                                  "AccountSettingsBtnCancel",
                                  "Cancel"
                                )}
                              </Button>
                              <Button
                                className={`btn bg-primary text-white ${
                                  (!redCactusTokenSaved &&                                   
                                  (!formRenderProps.allowSubmit ||
                                  !formRenderProps.modified ||
                                  !formRenderProps.valid)) ||
                                  buttonStateTracker ||
                                  redCactusTokenEditing ||
                                  masterData?.telepoDisableConfig?.systemSetting
                                    .account.disableChanges
                                    ? "disabledBtn"
                                    : ""
                                }`}
                                disabled={
                                  (!redCactusTokenSaved &&                                   
                                  (!formRenderProps.allowSubmit ||
                                  !formRenderProps.modified ||
                                  !formRenderProps.valid)) ||
                                  buttonStateTracker ||
                                  redCactusTokenEditing ||
                                  masterData?.telepoDisableConfig?.systemSetting
                                    .account.disableChanges
                                }
                              >
                                {updating ? (
                                  <Loader
                                    size={"small"}
                                    type={"infinite-spinner"}
                                    themeColor={"light"}
                                  />
                                ) : (
                                  <>
                                    {trans.fetchLabelKeyTranslation(
                                      "AccountSettingsBtnSave",
                                      "Save"
                                    )}
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </FormElement>
                  )}
                />
              )}
              <div>
                <Link
                  to="/customer-platform"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`btn btn-link text-primary p-0 m-t-20 fs-14 iconBtn`}
                >
                  <i className="bi bi-link-45deg m-r-5"></i>
                  {trans.fetchLabelKeyTranslation(
                    "CustomerPlatformDetailLink",
                    "Platform Details"
                  )}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;
